import React from "react";

export const CARD_BORDER = "hsl(210,30%,85%)";

export function Card({
  children,
  className,
  style,
  padded = true,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  padded?: boolean;
}) {
  return (
    <div
      className={className}
      style={{
        background: "white",
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: "0.75rem",
        padding: padded ? "14px 16px" : 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  icon,
  title,
  count,
  action,
  accent,
}: {
  icon?: React.ReactNode;
  title: string;
  count?: number;
  action?: React.ReactNode;
  accent?: string;
}) {
  return (
    <div className="mb-3 flex items-center gap-2">
      {icon}
      <h3 className="text-[13px] font-semibold text-foreground">{title}</h3>
      {typeof count === "number" && (
        <span
          className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold"
          style={{
            background: accent ?? "hsl(210,16%,93%)",
            color: accent ? "white" : "hsl(215,16%,40%)",
          }}
        >
          {count}
        </span>
      )}
      {action && <div className="ml-auto">{action}</div>}
    </div>
  );
}

export function ViewAllLink({ href }: { href: string }) {
  return (
    <a
      href={href}
      className="text-[12px] font-medium text-primary hover:underline"
    >
      View all →
    </a>
  );
}

export function SkeletonRow({ height = 38 }: { height?: number }) {
  return (
    <div
      className="mb-2 animate-pulse rounded"
      style={{
        height,
        background: "hsl(210,16%,93%)",
      }}
    />
  );
}

export function Empty({
  icon,
  text,
  variant = "neutral",
}: {
  icon: React.ReactNode;
  text: string;
  variant?: "neutral" | "success";
}) {
  const color =
    variant === "success"
      ? "hsl(142,72%,28%)"
      : "hsl(215,16%,47%)";
  const bg =
    variant === "success" ? "hsl(142,55%,96%)" : "transparent";
  return (
    <div
      className="flex flex-col items-center justify-center rounded-lg py-6 text-center"
      style={{ background: bg, color }}
    >
      {icon}
      <div className="mt-2 text-[13px]">{text}</div>
    </div>
  );
}

export function priorityDot(priority: string): string {
  switch (priority.toUpperCase()) {
    case "CRITICAL":
      return "hsl(0,84%,55%)";
    case "HIGH":
      return "hsl(22,90%,50%)";
    case "MEDIUM":
      return "hsl(38,92%,50%)";
    case "LOW":
    default:
      return "hsl(215,16%,70%)";
  }
}

export function Dot({ color }: { color: string }) {
  return (
    <span
      className="inline-block shrink-0 rounded-full"
      style={{ width: 8, height: 8, background: color }}
    />
  );
}

export function formatDateShort(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  });
}

export function relativeDueLabel(iso: string | null): {
  text: string;
  color: string;
  pill: string | null;
} {
  if (!iso) return { text: "—", color: "hsl(215,16%,47%)", pill: null };
  const due = new Date(iso);
  const now = new Date();
  const msPerDay = 24 * 3600 * 1000;
  const startToday = new Date(now);
  startToday.setHours(0, 0, 0, 0);
  const startDue = new Date(due);
  startDue.setHours(0, 0, 0, 0);
  const diffDays = Math.round(
    (startDue.getTime() - startToday.getTime()) / msPerDay,
  );
  if (diffDays < 0) {
    return {
      text: formatDateShort(iso),
      color: "hsl(0,70%,40%)",
      pill: "Overdue",
    };
  }
  if (diffDays === 0) {
    return {
      text: "Today",
      color: "hsl(35,90%,32%)",
      pill: null,
    };
  }
  if (diffDays === 1) {
    return {
      text: "Tomorrow",
      color: "hsl(210,100%,35%)",
      pill: null,
    };
  }
  return {
    text: formatDateShort(iso),
    color: "hsl(215,16%,47%)",
    pill: null,
  };
}

export function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}
